import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Button,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ArrowBack as ArrowBackIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Undo as UndoIcon,
  Replay as RetryIcon,
} from '@mui/icons-material';

import GridContainer from '@/components/Grid/GridContainer';
import Card from '@/components/Card/Card';
import CardHeader from '@/components/Card/CardHeader';
import CardBody from '@/components/Card/CardBody';
import DataTable from '@/components/DataTable/DataTable';
import { TransactionsApi } from '@/api/clients/transactions/transactions.api';
import { useConfirmation } from '@/context/ConfirmationContext';
import {
  isPendingTransactionStatus,
  isReturnedAvailableToReclaimTransactionStatus,
  isReturnedPendingReturnToBankTransactionStatus,
  isReturnedTransactionStatus,
  isStartedTransactionStatus,
  TRANSACTION_STATUSES,
} from '@/utility';
import TransactionIdColumn from '@/components/DataTable/columns/TransactionIdColumn';
import TransactionStatusChip from '@/components/TransactionStatusChip';
import EllipsisText from '@/components/EllipsisText';
import TransferACHInformation from '@/components/TransferACHInformation';
import useTranslatedTransactionStatuses from '@/hooks/useTranslatedTransactionStatuses';

const TransactionDetails = () => {
  const { t } = useTranslation();
  const { getTranslatedStatus } = useTranslatedTransactionStatuses();
  const { transferId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { requestConfirmation } = useConfirmation();

  const [transfer, setTransfer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const fetchTransferDetails = async () => {
      try {
        setLoading(true);
        const response = await TransactionsApi.getTransferDetails(transferId);
        setTransfer(response.data);
      } catch (err) {
        setError(err.message || t('transactionDetails.failedToFetch'));
      } finally {
        setLoading(false);
      }
    };

    if (transferId) {
      fetchTransferDetails();
    }
  }, [transferId]);

  const handleTransferBackAction = async (isRetry = false) => {
    const confirmMessage = isRetry
      ? t('transactions.confirmations.retryTransferBack', { transferId })
      : t('transactions.confirmations.createTransferBack', { transferId });

    requestConfirmation(confirmMessage, async () => {
      try {
        setActionLoading(true);
        await TransactionsApi.createTransferBack(transferId);
      } catch (error) {
        console.error(
          `Error ${isRetry ? 'retrying' : 'creating'} transfer back:`,
          error,
        );
      } finally {
        try {
          const refreshResponse = await TransactionsApi.getTransferDetails(
            transferId,
          );
          setTransfer(refreshResponse.data);
        } catch (refreshError) {
          console.error('Error refreshing transfer details:', refreshError);
        }
        setActionLoading(false);
      }
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color='error'>
          {t('transactionDetails.error', { message: error })}
        </Typography>
      </Box>
    );
  }

  if (!transfer) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>{t('transactionDetails.notFound')}</Typography>
      </Box>
    );
  }

  const buildStepsFromHistory = (history) => {
    return history.map((entry) => {
      const timestamp = new Date(entry.created_at).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });

      const paymentStatus = entry.refund_status?.startsWith('Payment ')
        ? entry.refund_status.replace('Payment ', '')
        : null;

      let state = 'neutral';
      let label = entry.refund_status;

      if (paymentStatus) {
        label = `Payment ${getTranslatedStatus(paymentStatus)}`;
      }

      if (
        entry.refund_status?.toLowerCase().includes('error') ||
        isReturnedTransactionStatus(paymentStatus)
      ) {
        state = 'error';
      } else if (
        isPendingTransactionStatus(paymentStatus) ||
        isReturnedPendingReturnToBankTransactionStatus(paymentStatus)
      ) {
        state = 'warning';
      } else if (
        paymentStatus === TRANSACTION_STATUSES.SUCCESS ||
        isReturnedAvailableToReclaimTransactionStatus(paymentStatus) ||
        entry.refund_status?.toLowerCase().includes('completed')
      ) {
        state = 'success';
      } else if (isStartedTransactionStatus(paymentStatus)) {
        state = 'neutral';
      }

      return {
        label: label,
        sublabel: timestamp,
        state: state,
        updatedBy: entry.updated_by,
        reason: entry.reason,
        transferStatus: entry.transfer_status,
      };
    });
  };

  const TransactionStepper = ({
    title,
    history,
    transaction,
    showCreateButton,
    showRetryButton,
  }) => {
    const steps = buildStepsFromHistory(history);

    const CustomStepIcon = (props) => {
      const { icon } = props;
      const stepData = steps[icon - 1];

      if (!stepData) return <div>{icon}</div>;

      switch (stepData.state) {
        case 'error':
          return <ErrorIcon sx={{ color: 'error.main' }} />;
        case 'warning':
          return <WarningIcon sx={{ color: 'warning.main' }} />;
        case 'pending':
          return <InfoIcon sx={{ color: 'info.main' }} />;
        case 'success':
          return <CheckCircleIcon sx={{ color: 'success.main' }} />;
        case 'neutral':
          return <InfoIcon sx={{ color: 'info.main' }} />;
        default:
          return <div>{icon}</div>;
      }
    };

    const getStepProps = (step) => {
      const labelProps = {};

      if (step.sublabel) {
        labelProps.optional = (
          <Typography variant='caption' color='text.secondary'>
            {step.sublabel}
          </Typography>
        );
      }

      if (step.state === 'error') {
        labelProps.error = true;
      }

      return labelProps;
    };

    const detailColumns = transaction
      ? [
          {
            key: 'id',
            label: t('transactionDetails.table.transactionId'),
            render: (value) => (
              <TransactionIdColumn
                value={value}
                enableNavigation={true}
                enableCopy={true}
              />
            ),
          },
          {
            key: 'external_id',
            label: t('transactionDetails.table.externalId'),
            copyable: true,
            render: (value) => (
              <Typography variant='body2' sx={{ fontFamily: 'monospace' }}>
                {value || '–'}
              </Typography>
            ),
          },
          {
            key: 'original_transaction_id',
            label: t('transactionDetails.table.originalTransactionId'),
            render: (value) => (
              <TransactionIdColumn
                value={value}
                enableNavigation={true}
                enableCopy={true}
              />
            ),
          },
          {
            key: 'transfer_back_id',
            label: t('transactionDetails.table.transferBackId'),
            render: (value) => (
              <TransactionIdColumn
                value={value}
                enableNavigation={true}
                enableCopy={true}
              />
            ),
          },
          {
            key: 'amount',
            label: t('transactionDetails.table.amount'),
            render: (value) => (
              <Typography
                variant='body2'
                color='primary'
                sx={{ fontWeight: 600 }}>
                {formatCurrency(value)}
              </Typography>
            ),
          },
          {
            key: 'transaction_status',
            label: t('transactionDetails.table.status'),
            render: (value) => <TransactionStatusChip status={value} />,
          },
          {
            key: 'error_message',
            label: t('transactionDetails.table.errorMessage'),
            render: (value) => (
              <EllipsisText
                text={value || '–'}
                variant='body2'
                color={value ? 'error.main' : 'text.primary'}
                maxWidth={200}
              />
            ),
          },
          {
            key: 'return_code',
            label: t('transactionDetails.table.returnCode'),
            render: (value) => (
              <Typography variant='body2' sx={{ fontFamily: 'monospace' }}>
                {value || '–'}
              </Typography>
            ),
          },
          {
            key: 'return_reason',
            label: t('transactionDetails.table.returnReason'),
            render: (value) => (
              <EllipsisText
                text={value || '–'}
                variant='body2'
                maxWidth={250}
              />
            ),
          },
          {
            key: 'return_description',
            label: t('transactionDetails.table.returnDescription'),
            render: (value) => (
              <EllipsisText
                text={value || '–'}
                variant='body2'
                maxWidth={250}
              />
            ),
          },
          {
            key: 'created_at',
            label: t('transactionDetails.table.createdAt'),
            render: (value) => (
              <Typography variant='body2'>
                {value ? new Date(value).toLocaleString() : '–'}
              </Typography>
            ),
          },
          {
            key: 'updated_at',
            label: t('transactionDetails.table.updatedAt'),
            render: (value) => (
              <Typography variant='body2'>
                {value ? new Date(value).toLocaleString() : '–'}
              </Typography>
            ),
          },
        ].filter((col) => {
          const alwaysShowColumns = ['id', 'amount', 'status'];
          return (
            alwaysShowColumns.includes(col.key) ||
            transaction[col.key] !== undefined
          );
        })
      : [];

    const detailData = transaction ? [transaction] : [];

    return (
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}>
          <Typography variant='h6' sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Box>

        <Stepper
          activeStep={steps.length}
          orientation='horizontal'
          sx={{ mb: 3 }}>
          {steps.map((step, index) => {
            const labelProps = getStepProps(step, index);
            const isLastStep = index === steps.length - 1;
            const showCreateButtonHere =
              showCreateButton && isLastStep && step.state === 'error';
            const showRetryButtonHere =
              showRetryButton && isLastStep && step.state === 'error';

            return (
              <Step key={index}>
                <StepLabel {...labelProps} StepIconComponent={CustomStepIcon}>
                  <Box>
                    <Typography variant='body2' sx={{ fontWeight: 500 }}>
                      {step.label}
                    </Typography>
                    {step.updatedBy && (
                      <EllipsisText
                        text={t('transactionDetails.updatedBy', {
                          user: step.updatedBy,
                        })}
                        variant='caption'
                        color='text.secondary'
                        maxWidth={100}
                        sx={{ display: 'block' }}
                      />
                    )}
                    {step.reason && (
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ display: 'block' }}>
                        {step.reason}
                      </Typography>
                    )}
                    {(showCreateButtonHere || showRetryButtonHere) && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                        {showCreateButtonHere && (
                          <Tooltip
                            title={t(
                              'transactionDetails.actions.createTransferBack',
                            )}>
                            <Button
                              variant='contained'
                              size='small'
                              startIcon={
                                actionLoading ? (
                                  <CircularProgress
                                    size={16}
                                    sx={{ color: 'white' }}
                                  />
                                ) : (
                                  <UndoIcon sx={{ color: 'white' }} />
                                )
                              }
                              onClick={() => handleTransferBackAction(false)}
                              disabled={actionLoading}
                              sx={{
                                fontSize: '0.75rem',
                                color: 'white',
                              }}>
                              {actionLoading
                                ? t('transactionDetails.buttons.creating')
                                : t(
                                    'transactionDetails.buttons.createTransferBack',
                                  )}
                            </Button>
                          </Tooltip>
                        )}
                        {showRetryButtonHere && (
                          <Tooltip
                            title={t(
                              'transactionDetails.actions.retryTransferBack',
                            )}>
                            <Button
                              variant='contained'
                              size='small'
                              startIcon={
                                actionLoading ? (
                                  <CircularProgress
                                    size={16}
                                    sx={{ color: 'white' }}
                                  />
                                ) : (
                                  <RetryIcon sx={{ color: 'white' }} />
                                )
                              }
                              onClick={() => handleTransferBackAction(true)}
                              disabled={actionLoading}
                              sx={{
                                fontSize: '0.75rem',
                                color: 'white',
                              }}>
                              {actionLoading
                                ? t('transactionDetails.buttons.retrying')
                                : t(
                                    'transactionDetails.buttons.retryTransferBack',
                                  )}
                            </Button>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  </Box>
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>

        {detailData.length > 0 && (
          <DataTable
            columns={detailColumns}
            testData={detailData}
            enableFilters={false}
            enableColumnPicker={false}
            enableSorting={false}
            enablePagination={false}
            enablePinning={false}
            tableProps={{
              sx: {
                '& .MuiTableContainer-root': {
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                },
              },
            }}
          />
        )}
      </Box>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  return (
    <Box sx={{ p: 2 }}>
      <GridContainer>
        <Card>
          <CardHeader color='primary'>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={() => navigate(`/admin/transfers${location.search}`)}
                sx={{
                  mr: 2,
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}>
                <ArrowBackIcon sx={{ color: 'white' }} />
              </IconButton>
              <Typography
                variant='h6'
                sx={{ color: 'white', fontWeight: 500 }}
                component='h4'>
                {t('transactionDetails.title')}
              </Typography>
            </Box>
          </CardHeader>
          <CardBody>
            {transfer.transaction_history &&
              transfer.transaction_history.length > 0 && (
                <TransactionStepper
                  title={t('transactionDetails.originalTransaction')}
                  history={transfer.transaction_history}
                  transaction={transfer}
                  showCreateButton={transfer.can_create_transfer_back}
                />
              )}

            <Box sx={{ mt: 4 }}>
              <Typography variant='h6' sx={{ fontWeight: 600, mb: 2 }}>
                {t('achDetails.title')}
              </Typography>
              <TransferACHInformation
                transferId={transfer.external_id}
                destinationBankAccount={transfer.destination_bank_account}
                sourceAchDetails={transfer.source_ach_details}
                destinationAchDetails={transfer.destination_ach_details}
              />
            </Box>

            {transfer.transfer_backs && transfer.transfer_backs.length > 0 && (
              <Box>
                {transfer.transfer_backs.map((transferBack, index) => {
                  const isLastTransferBack =
                    index === transfer.transfer_backs.length - 1;
                  const hasError = transferBack.history?.some((entry) =>
                    entry.refund_status?.toLowerCase().includes('error'),
                  );
                  const showRetryButton =
                    transfer.can_retry_transfer_back &&
                    isLastTransferBack &&
                    hasError;

                  return (
                    <TransactionStepper
                      key={transferBack.id}
                      title={t('transactionDetails.transferBack', {
                        number: index + 1,
                      })}
                      history={transferBack.history}
                      transaction={transferBack}
                      showRetryButton={showRetryButton}
                    />
                  );
                })}
              </Box>
            )}
          </CardBody>
        </Card>
      </GridContainer>
    </Box>
  );
};

export default TransactionDetails;
